import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import OverviewTab from "./tabs/OverviewTab";
import ScheduleTab from "./tabs/ScheduleTab";
import DropControlTab from "./tabs/DropControlTab";
import LinksTab from "./tabs/LinksTab";
import UsersTab from "./tabs/UsersTab";
import CardImpactPanel from "./CardImpactPanel";
import DropLobbySimPanel from "../components/DropLobbySimPanel";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightAndDownLeftFromCenter } from "@fortawesome/free-solid-svg-icons";
import { useAppSelector } from "../../../store/hooks";
import { checkRole } from "../../../components/recipe_page/recipe_page";
import {
  AdminCardRow,
  CardEffectConfig,
  CardRarity,
  useCreateAdminCardMutation,
  useDeleteAdminCardMutation,
  useGenerateCardArtImagesMutation,
  useGenerateCardArtPromptMutation,
  useGetAdminCardsQuery,
  useGetAdminRuntimeActionsQuery,
  useGetAdminRuntimeAotdStateQuery,
  useGetAdminRuntimeEffectsQuery,
  useGetAdminRuntimeEventsQuery,
  useGetTodayDropQuery,
  useRecommendCardMutation,
  useSelectCardArtImageMutation,
  useToggleAdminCardMutation,
  useUpdateAdminCardMutation,
} from "../api/respectCardsApi";
import type { CardDefinition, DropCard } from "../api/respectCardsApi";
import styled, { keyframes } from "styled-components";
import RespectCard from "../components/RespectCard";
import { useConfirm } from "../../../ui/components/ConfirmDialog";
import { useGetAgentGameCurrentQuery } from "../../../services/rtkApi/agentGameApi";
import {
  CARD_CATEGORY_OPTIONS,
  CARD_EDITOR_PRESETS,
  CONSUME_ON_OPTIONS,
  EFFECT_TARGET_OPTIONS,
  EFFECT_TRIGGER_OPTIONS,
  KNOWN_EFFECT_SCOPE_OPTIONS,
  KNOWN_EFFECT_TYPE_OPTIONS,
  OWNERSHIP_CHECK_OPTIONS,
  RECIPIENT_FIELD_OPTIONS,
  SURFACE_OPTIONS,
  VISIBILITY_AUDIENCE_OPTIONS,
  collectStringOptions,
  getCardEditorPreset,
  inferCardEditorPresetId,
  slugifyCardName,
  type CardEditorPresetId,
  type SelectOption,
} from "./cardEditorOptions";
import "../styles/cards.css";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  max-width: 1240px;
  margin: 0 auto;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #222;
  margin-bottom: 0.5rem;
`;

const Section = styled.section`
  width: 100%;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 12px;
`;

const RuntimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const RuntimeTile = styled.div`
  border: 1px solid #ece6f7;
  border-radius: 8px;
  background: #faf7ff;
  padding: 12px;
`;

const RuntimeKicker = styled.div`
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6d5a9a;
  margin-bottom: 6px;
`;

const RuntimeValue = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #2e2550;
  margin-bottom: 4px;
`;

const Mono = styled.code`
  font-size: 0.8rem;
  color: #4b3b72;
  word-break: break-word;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  th,
  td {
    text-align: left;
    padding: 8px;
    border-bottom: 1px solid #eee;
  }
  th {
    color: #666;
    font-weight: 600;
  }
  tr:hover td {
    background: #fafafa;
  }
`;

const Row = styled.div.attrs({ className: "cardsa-row" })`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  align-items: flex-end;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;
  color: #555;
  gap: 4px;
  min-width: 140px;
  flex: 1;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
  background: #fff;
`;

const TextArea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
  min-height: 72px;
  width: 100%;
  box-sizing: border-box;
`;

const Btn = styled.button<{ variant?: "primary" | "danger" | "ghost" }>`
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid
    ${(p) => (p.variant === "danger" ? "#c62828" : p.variant === "primary" ? "#4f3e76" : "#ccc")};
  background: ${(p) =>
    p.variant === "danger" ? "#ffebee" : p.variant === "primary" ? "#f5f1ff" : "#fff"};
  color: ${(p) => (p.variant === "danger" ? "#c62828" : p.variant === "primary" ? "#4f3e76" : "#333")};
  cursor: pointer;
  font-size: 0.85rem;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ImagePick = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const ImagePreviewTile = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const PickImageButton = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  background: none;
  cursor: pointer;
  border-radius: 10px;
  img {
    width: 160px;
    height: 160px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #ddd;
    display: block;
    vertical-align: top;
  }
  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
  &:not(:disabled):hover img {
    border-color: #4f3e76;
  }
`;

const ExpandImageButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #fff;
  color: #4f3e76;
  cursor: pointer;
  font-size: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  &:hover {
    background: #f5f1ff;
    border-color: #4f3e76;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
`;

const LightboxPanel = styled.div`
  position: relative;
  max-width: min(96vw, 960px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 8px;
`;

const LightboxImg = styled.img`
  max-width: 100%;
  max-height: calc(90vh - 100px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.45);
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: #222;
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  &:hover {
    background: #f0f0f0;
  }
`;

const LightboxHint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  max-width: 420px;
  line-height: 1.4;
`;

const EditorLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: start;
  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 1fr) 320px;
  }
`;

const PreviewAside = styled.aside`
  @media (min-width: 960px) {
    position: sticky;
    top: 12px;
  }
`;

const PreviewWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 12px;
  background: linear-gradient(145deg, #e8e8e8 0%, #f5f5f5 100%);
  border-radius: 10px;
  border: 1px solid #ddd;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const InlineSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 8px;
  border: 2px solid rgba(79, 62, 118, 0.25);
  border-top-color: #4f3e76;
  border-radius: 50%;
  vertical-align: middle;
  animation: ${spin} 0.65s linear infinite;
`;

const ArtStatus = styled.p`
  font-size: 0.82rem;
  color: #555;
  margin: 8px 0 0;
  line-height: 1.4;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const HelperText = styled.p`
  margin: 6px 0 0;
  font-size: 0.78rem;
  color: #666;
  line-height: 1.45;
`;

const InfoCard = styled.div`
  border: 1px solid #ece6f7;
  border-radius: 10px;
  background: #faf7ff;
  padding: 12px 14px;
  margin-bottom: 12px;
`;

const EditorTopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0 12px;
  margin: -4px 0 14px;
  background: #fff;
  border-bottom: 1px solid #ece6f7;
`;

const EditorTopBarActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EditorGroup = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #f0eaf6;

  &:first-of-type {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

const EditorGroupTitle = styled.h4`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6d5a9a;
  margin: 0 0 10px;
`;

const PresetCard = styled.div`
  border: 1px solid #ece6f7;
  border-radius: 10px;
  background: #faf7ff;
  padding: 12px 14px;
  margin-bottom: 14px;
`;

const SubTitle = styled.h3`
  font-size: 0.95rem;
  color: #333;
  margin: 0 0 8px;
`;

const Details = styled.details`
  margin-top: 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  background: #fcfcfc;
  padding: 10px 12px;

  summary {
    cursor: pointer;
    font-size: 0.84rem;
    color: #4f3e76;
    font-weight: 600;
  }
`;

const defaultEffect = (): CardEffectConfig => ({
  type: "custom",
  target: "self",
  scope: "aotd",
  trigger: "on_play",
  params: {},
});

const GALLERY_RARITY_ORDER: CardRarity[] = ["legendary", "epic", "rare", "common"];

function galleryRaritySortIndex(r: string | undefined): number {
  const i = GALLERY_RARITY_ORDER.indexOf(r as CardRarity);
  return i === -1 ? 99 : i;
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function previewJson(value: unknown, max = 160): string {
  if (value === null || value === undefined) {
    return "—";
  }

  try {
    const raw = JSON.stringify(value);
    return raw.length <= max ? raw : `${raw.slice(0, max - 1)}…`;
  } catch {
    return String(value);
  }
}

function isRecommendedRow(x: unknown): x is {
  slug: string;
  name: string;
  description: string;
  flavor: string;
  rarity: CardRarity;
  category: string;
  tags: string[];
  effect: CardEffectConfig;
  durationHours: number | null;
  droppable: boolean;
  stackable: boolean;
  maxPerHand: number;
  imagePrompt: string;
} {
  return Boolean(x && typeof x === "object");
}

function toPrettyJson(value: Record<string, unknown>): string {
  return JSON.stringify(value, null, 2);
}

function parseObjectJson(
  raw: string,
): { value: Record<string, unknown> | null; error: string | null } {
  try {
    const parsed = JSON.parse(raw || "{}") as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { value: null, error: "JSON must be an object." };
    }
    return { value: parsed as Record<string, unknown>, error: null };
  } catch (error) {
    return {
      value: null,
      error: error instanceof Error ? error.message : "Invalid JSON.",
    };
  }
}

function cloneObject(value: Record<string, unknown>): Record<string, unknown> {
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
}

function optionLabel(value: string, options: SelectOption[]): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

type CardsAdminTab =
  | "overview"
  | "catalog"
  | "schedule"
  | "drop"
  | "sim"
  | "users"
  | "links"
  | "game"
  | "runtime";

// cards-drop-lobby phase 12 — the sim-lobby panel is dev-only (its backend
// routes 404 in production), so the tab is hidden outside dev. Mirrors the
// `DevCardSimulatorPanel` gating on `CardsPage`.
const IS_DEV = process.env.NODE_ENV !== "production";

const TAB_OPTIONS: Array<{ id: CardsAdminTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "catalog", label: "Catalog" },
  { id: "schedule", label: "Schedule" },
  { id: "drop", label: "Drop control" },
  ...(IS_DEV ? [{ id: "sim" as const, label: "🧪 Sim lobby" }] : []),
  { id: "users", label: "Users" },
  { id: "links", label: "Links" },
  { id: "game", label: "Game" },
  { id: "runtime", label: "Runtime inspector" },
];

const CardsAdminPage: React.FC = () => {
  const confirm = useConfirm();
  const user = useAppSelector((state) => state.auth);
  const isAdmin = checkRole(user.role ?? "", "admin");
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = (searchParams.get("tab") || "catalog") as CardsAdminTab;
  const activeTab: CardsAdminTab = TAB_OPTIONS.some((t) => t.id === tabParam)
    ? tabParam
    : "catalog";
  const setActiveTab = (next: CardsAdminTab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const { data: adminData, isLoading: adminLoading } = useGetAdminCardsQuery(undefined, {
    skip: !isAdmin || user.bootstrapStatus !== "ready",
  });
  const {
    data: runtimeAotdData,
    isLoading: runtimeAotdLoading,
    refetch: refetchRuntimeAotd,
  } = useGetAdminRuntimeAotdStateQuery(undefined, {
    skip: !isAdmin || user.bootstrapStatus !== "ready",
  });
  const {
    data: runtimeEventsData,
    isLoading: runtimeEventsLoading,
    refetch: refetchRuntimeEvents,
  } = useGetAdminRuntimeEventsQuery({ limit: 25 }, {
    skip: !isAdmin || user.bootstrapStatus !== "ready",
  });
  const {
    data: runtimeEffectsData,
    isLoading: runtimeEffectsLoading,
    refetch: refetchRuntimeEffects,
  } = useGetAdminRuntimeEffectsQuery({ limit: 25 }, {
    skip: !isAdmin || user.bootstrapStatus !== "ready",
  });
  const {
    data: runtimeActionsData,
    isLoading: runtimeActionsLoading,
    refetch: refetchRuntimeActions,
  } = useGetAdminRuntimeActionsQuery({ limit: 25 }, {
    skip: !isAdmin || user.bootstrapStatus !== "ready",
  });
  const { data: drop, isLoading: dropLoading } = useGetTodayDropQuery();
  const { data: currentAgentGameData, isLoading: currentAgentGameLoading } = useGetAgentGameCurrentQuery(
    undefined,
    {
      skip: !isAdmin || user.bootstrapStatus !== "ready",
    },
  );

  const [createCard] = useCreateAdminCardMutation();
  const [updateCard] = useUpdateAdminCardMutation();
  const [deleteCard] = useDeleteAdminCardMutation();
  const [toggleCard] = useToggleAdminCardMutation();
  const [genPrompt, { isLoading: isGeneratingPrompt }] = useGenerateCardArtPromptMutation();
  const [genImages, { isLoading: isGeneratingImages }] = useGenerateCardArtImagesMutation();
  const [selectImage, { isLoading: isSavingArtImage }] = useSelectCardArtImageMutation();
  const [recommendCard] = useRecommendCardMutation();

  const [creatingNew, setCreatingNew] = useState(false);
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [flavor, setFlavor] = useState("");
  const [rarity, setRarity] = useState<CardRarity>("common");
  const [category, setCategory] = useState("aotd");
  const [tagsStr, setTagsStr] = useState("");
  const [emoji, setEmoji] = useState("🃏");
  const [durationHours, setDurationHours] = useState<string>("");
  const [droppable, setDroppable] = useState(true);
  const [stackable, setStackable] = useState(false);
  const [maxPerHand, setMaxPerHand] = useState("1");
  const [effectType, setEffectType] = useState("custom");
  const [effectTarget, setEffectTarget] = useState<CardEffectConfig["target"]>("self");
  const [effectScope, setEffectScope] = useState("aotd");
  const [effectTrigger, setEffectTrigger] = useState<CardEffectConfig["trigger"]>("on_play");
  const [effectParamsJson, setEffectParamsJson] = useState("{}");
  const [effectPriority, setEffectPriority] = useState("");
  const [effectBehaviorModule, setEffectBehaviorModule] = useState("");
  const [effectBehaviorJson, setEffectBehaviorJson] = useState("");
  const [selectedPresetId, setSelectedPresetId] = useState<CardEditorPresetId>("custom");

  const [imagePrompt, setImagePrompt] = useState("");
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const pendingImageGenCount = useRef<1 | 2>(2);

  useEffect(() => {
    if (!lightboxSrc) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxSrc(null);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxSrc]);

  const [recommendIdea, setRecommendIdea] = useState("");
  const [galleryRarity, setGalleryRarity] = useState<"" | CardRarity>("");
  const [galleryCategory, setGalleryCategory] = useState("");
  const [gallerySearch, setGallerySearch] = useState("");
  const [selectedEditingSlug, setSelectedEditingSlug] = useState<string | null>(null);
  const [inlineEditorTarget, setInlineEditorTarget] = useState<HTMLDivElement | null>(null);
  // Stable callback ref — an inline arrow ref would be a fresh function each
  // render, causing React to detach/reattach during commit. The cleanup phase
  // would set the target to null, which falls through to the inline-render
  // fallback below, remounting the editor inputs and stealing focus from
  // whatever the user is typing into.
  const handleEditorRowRef = useCallback((el: HTMLDivElement | null) => {
    setInlineEditorTarget((prev) => (prev === el ? prev : el));
  }, []);

  const adminCards = useMemo<AdminCardRow[]>(() => adminData?.cards ?? [], [adminData?.cards]);
  // Phase 1 cards-launch: the public /cards/drop/today no longer exposes
  // the per-slug `cards[]` array. The admin tier breakdown moves to the
  // schedule endpoint in Phase 2 — render an empty list here for now to
  // keep types compiling without redesigning the panel.
  const dropCards = useMemo<DropCard[]>(() => [], []);
  const runtimeEffects = useMemo(() => runtimeEffectsData?.effects ?? [], [runtimeEffectsData?.effects]);
  const runtimeEvents = useMemo(() => runtimeEventsData?.events ?? [], [runtimeEventsData?.events]);
  const runtimeActions = useMemo(() => runtimeActionsData?.actions ?? [], [runtimeActionsData?.actions]);
  const currentAgentGame = currentAgentGameData?.game ?? null;

  const categorySuggestions = useMemo(() => {
    const s = new Set<string>();
    adminCards.forEach((c) => s.add(c.category));
    CARD_CATEGORY_OPTIONS.forEach((option) => s.add(option.value));
    return [...s].sort();
  }, [adminCards]);

  const categoryOptions = useMemo(
    () =>
      collectStringOptions(
        [...adminCards.map((card) => card.category), category],
        CARD_CATEGORY_OPTIONS.map((option) => option.value),
      ),
    [adminCards, category],
  );

  const effectTypeOptions = useMemo(
    () =>
      collectStringOptions(
        [...adminCards.map((card) => card.effect?.type), effectType],
        KNOWN_EFFECT_TYPE_OPTIONS.map((option) => option.value),
      ),
    [adminCards, effectType],
  );

  const effectScopeOptions = useMemo(
    () =>
      collectStringOptions(
        [...adminCards.map((card) => card.effect?.scope), effectScope],
        KNOWN_EFFECT_SCOPE_OPTIONS.map((option) => option.value),
      ),
    [adminCards, effectScope],
  );

  const behaviorModuleOptions = useMemo(
    () =>
      collectStringOptions(
        [...adminCards.map((card) => card.effect?.behaviorModule), effectBehaviorModule],
        [],
      ),
    [adminCards, effectBehaviorModule],
  );

  const agentInfluenceCards = useMemo(
    () => adminCards.filter((card) => String(card.category || "") === "agent-influence"),
    [adminCards],
  );

  const galleryRarityCounts = useMemo(() => {
    const counts: Partial<Record<CardRarity, number>> = {};
    for (const c of adminCards) {
      const r = (c.rarity || "common") as CardRarity;
      counts[r] = (counts[r] ?? 0) + 1;
    }
    return counts;
  }, [adminCards]);

  const galleryFilteredCards = useMemo(() => {
    const q = gallerySearch.trim().toLowerCase();
    let list = [...adminCards];
    if (galleryRarity) {
      list = list.filter((c) => c.rarity === galleryRarity);
    }
    if (galleryCategory) {
      list = list.filter(
        (c) => (typeof c.category === "string" ? c.category : "") === galleryCategory,
      );
    }
    if (q) {
      list = list.filter((c) => {
        const slug = String(c.slug ?? c.id).toLowerCase();
        const name = c.name.toLowerCase();
        return slug.includes(q) || name.includes(q);
      });
    }
    list.sort((a, b) => {
      const dr = galleryRaritySortIndex(a.rarity) - galleryRaritySortIndex(b.rarity);
      if (dr !== 0) {
        return dr;
      }
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [adminCards, galleryCategory, galleryRarity, gallerySearch]);

  const parsedEffectParamsState = useMemo(
    () => parseObjectJson(effectParamsJson),
    [effectParamsJson],
  );
  const parsedEffectParams = parsedEffectParamsState.value;
  const effectParamsError = parsedEffectParamsState.error;

  const visibilityParams = useMemo(() => {
    const raw = parsedEffectParams?.visibility;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return {} as Record<string, unknown>;
    }
    return raw as Record<string, unknown>;
  }, [parsedEffectParams]);

  const selectedSurface =
    Array.isArray(visibilityParams.surfaces) &&
    typeof visibilityParams.surfaces[0] === "string"
      ? String(visibilityParams.surfaces[0])
      : "";

  const effectDurationSeconds =
    typeof parsedEffectParams?.durationSeconds === "number"
      ? String(parsedEffectParams.durationSeconds)
      : "";
  const effectMultiplier =
    typeof parsedEffectParams?.multiplier === "number"
      ? String(parsedEffectParams.multiplier)
      : "";
  const effectMaxBonus =
    typeof parsedEffectParams?.maxBonus === "number"
      ? String(parsedEffectParams.maxBonus)
      : "";
  const effectRecipientField =
    typeof parsedEffectParams?.recipientField === "string"
      ? parsedEffectParams.recipientField
      : "";
  const effectOwnershipCheck =
    typeof parsedEffectParams?.ownershipCheck === "string"
      ? parsedEffectParams.ownershipCheck
      : "";
  const effectConsumeOn =
    typeof parsedEffectParams?.consumeOn === "string"
      ? parsedEffectParams.consumeOn
      : "";
  const rewardObject =
    parsedEffectParams?.reward &&
    typeof parsedEffectParams.reward === "object" &&
    !Array.isArray(parsedEffectParams.reward)
      ? (parsedEffectParams.reward as Record<string, unknown>)
      : null;
  const rewardRespect =
    typeof rewardObject?.respect === "number" ? String(rewardObject.respect) : "";
  const effectBlockedMessage =
    typeof parsedEffectParams?.blockedMessage === "string"
      ? parsedEffectParams.blockedMessage
      : "";
  const effectPinDurationHours =
    typeof parsedEffectParams?.pinDurationHours === "number"
      ? String(parsedEffectParams.pinDurationHours)
      : "";
  const effectHighlightDurationHours =
    typeof parsedEffectParams?.highlightDurationHours === "number"
      ? String(parsedEffectParams.highlightDurationHours)
      : "";
  const effectTransferAmount =
    typeof parsedEffectParams?.amount === "number"
      ? String(parsedEffectParams.amount)
      : "";
  const effectTaxFree = parsedEffectParams?.taxFree === true;

  function replaceEffectParams(nextParams: Record<string, unknown>): void {
    setEffectParamsJson(toPrettyJson(nextParams));
  }

  function updateEffectParams(mutator: (draft: Record<string, unknown>) => void): void {
    const base = parsedEffectParams ? cloneObject(parsedEffectParams) : {};
    mutator(base);
    replaceEffectParams(base);
  }

  function setScalarEffectParam(
    key: string,
    rawValue: string,
    valueType: "string" | "number" | "boolean",
  ): void {
    updateEffectParams((draft) => {
      const trimmed = rawValue.trim();
      if (trimmed === "") {
        delete draft[key];
        return;
      }

      if (valueType === "number") {
        const parsed = Number.parseFloat(trimmed);
        if (!Number.isFinite(parsed)) {
          return;
        }
        draft[key] = parsed;
        return;
      }

      if (valueType === "boolean") {
        draft[key] = trimmed === "true";
        return;
      }

      draft[key] = rawValue;
    });
  }

  function updateVisibilityField(key: string, value: string | boolean): void {
    updateEffectParams((draft) => {
      const nextVisibility =
        draft.visibility && typeof draft.visibility === "object" && !Array.isArray(draft.visibility)
          ? cloneObject(draft.visibility as Record<string, unknown>)
          : {};

      if (typeof value === "string") {
        if (value.trim() === "") {
          delete nextVisibility[key];
        } else {
          nextVisibility[key] = value;
        }
      } else {
        nextVisibility[key] = value;
      }

      if (Object.keys(nextVisibility).length === 0) {
        delete draft.visibility;
      } else {
        draft.visibility = nextVisibility;
      }
    });
  }

  function updateVisibilitySurface(surface: string): void {
    updateEffectParams((draft) => {
      const nextVisibility =
        draft.visibility && typeof draft.visibility === "object" && !Array.isArray(draft.visibility)
          ? cloneObject(draft.visibility as Record<string, unknown>)
          : {};

      if (!surface.trim()) {
        delete nextVisibility.surfaces;
      } else {
        nextVisibility.surfaces = [surface];
      }

      if (Object.keys(nextVisibility).length === 0) {
        delete draft.visibility;
      } else {
        draft.visibility = nextVisibility;
      }
    });
  }

  function setRewardRespect(rawValue: string): void {
    updateEffectParams((draft) => {
      const trimmed = rawValue.trim();
      if (!trimmed) {
        delete draft.reward;
        return;
      }
      const parsed = Number.parseFloat(trimmed);
      if (!Number.isFinite(parsed)) {
        return;
      }
      draft.reward = { respect: parsed };
    });
  }

  const activePreset = useMemo(
    () => getCardEditorPreset(selectedPresetId),
    [selectedPresetId],
  );

  const previewCard = useMemo((): CardDefinition => {
    const slugKey = slug.trim() || "preview-card";
    const fromRow = adminCards.find((c) => (c.slug ?? c.id) === slugKey);
    const savedUrl = fromRow?.imageUrl;
    const draftArt =
      imagePreviews.length > 0
        ? imagePreviews[0]
        : savedUrl && String(savedUrl).length > 0
          ? savedUrl
          : null;
    const durRaw = durationHours.trim();
    const dur = durRaw === "" ? null : Number.parseFloat(durRaw);
    return {
      id: slugKey,
      slug: slugKey,
      name: name.trim() || "Card name",
      description: description.trim() || "Description text appears in the card body.",
      flavor: flavor.trim(),
      rarity,
      category: (category.trim() || "aotd") as CardDefinition["category"],
      emoji: emoji.trim() || "🃏",
      durationHours: dur !== null && Number.isFinite(dur) ? dur : null,
      droppable,
      tags: tagsStr
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      imageUrl: draftArt,
      stackable,
      maxPerHand: Math.max(1, Number.parseInt(maxPerHand, 10) || 1),
    };
  }, [
    slug,
    adminCards,
    imagePreviews,
    name,
    description,
    flavor,
    rarity,
    category,
    emoji,
    durationHours,
    droppable,
    tagsStr,
    stackable,
    maxPerHand,
  ]);

  function applyPreset(presetId: CardEditorPresetId): void {
    const preset = getCardEditorPreset(presetId);
    setSelectedPresetId(presetId);
    setCategory(preset.category);
    if (!tagsStr.trim() && preset.tags.length > 0) {
      setTagsStr(preset.tags.join(", "));
    }
    setEffectType(preset.effect.type);
    setEffectTarget(preset.effect.target);
    setEffectScope(preset.effect.scope);
    setEffectTrigger(preset.effect.trigger);
    setEffectParamsJson(toPrettyJson(preset.effect.params));
    setEffectPriority("");
    setEffectBehaviorModule("");
    setEffectBehaviorJson("");
  }

  function syncSlugFromName(): void {
    if (!creatingNew) {
      return;
    }
    const nextSlug = slugifyCardName(name);
    if (!nextSlug) {
      toast.error("enter a name first");
      return;
    }
    setSlug(nextSlug);
  }

  function loadCardIntoEditor(c: AdminCardRow | CardDefinition): void {
    const row = c as AdminCardRow;
    const targetSlug = c.slug ?? c.id ?? null;
    // toggle: clicking the already-open card closes the inline editor
    if (targetSlug && selectedEditingSlug === targetSlug) {
      setSelectedEditingSlug(null);
      return;
    }
    // catalog-only rows must be saved with "create" so they land in MongoDB
    setCreatingNew(row.persistedInDb === false);
    setSelectedEditingSlug(targetSlug);
    setSlug(c.slug ?? c.id);
    setName(c.name);
    setDescription(c.description);
    setFlavor(c.flavor ?? "");
    setRarity(c.rarity);
    setCategory(typeof c.category === "string" ? c.category : "aotd");
    setTagsStr((c.tags ?? []).join(", "));
    setEmoji(c.emoji);
    setDurationHours(c.durationHours === null || c.durationHours === undefined ? "" : String(c.durationHours));
    setDroppable(c.droppable !== false);
    setStackable(Boolean(c.stackable));
    setMaxPerHand(String(c.maxPerHand ?? 1));
    const eff = c.effect ?? defaultEffect();
    setSelectedPresetId(inferCardEditorPresetId(eff, typeof c.category === "string" ? c.category : "aotd"));
    setEffectType(eff.type);
    setEffectTarget(eff.target);
    setEffectScope(eff.scope);
    setEffectTrigger(eff.trigger);
    try {
      setEffectParamsJson(JSON.stringify(eff.params ?? {}, null, 2));
    } catch {
      setEffectParamsJson("{}");
    }
    setEffectPriority(
      typeof eff.priority === "number" && Number.isFinite(eff.priority)
        ? String(eff.priority)
        : "",
    );
    setEffectBehaviorModule(eff.behaviorModule ?? "");
    try {
      setEffectBehaviorJson(
        eff.behavior ? JSON.stringify(eff.behavior, null, 2) : "",
      );
    } catch {
      setEffectBehaviorJson("");
    }
    setImagePrompt(c.imagePrompt ?? "");
    setImagePreviews([]);
  }

  function startNewCard(): void {
    setCreatingNew(true);
    setSelectedEditingSlug("__new__");
    setSlug("");
    setName("");
    setDescription("");
    setFlavor("");
    setRarity("common");
    setCategory("aotd");
    setTagsStr("");
    setEmoji("🃏");
    setDurationHours("");
    setDroppable(true);
    setStackable(false);
    setMaxPerHand("1");
    setSelectedPresetId("custom");
    setEffectType("custom");
    setEffectTarget("self");
    setEffectScope("aotd");
    setEffectTrigger("on_play");
    setEffectParamsJson("{}");
    setEffectPriority("");
    setEffectBehaviorModule("");
    setEffectBehaviorJson("");
    setImagePrompt("");
    setImagePreviews([]);
  }

  function buildEffect(): CardEffectConfig {
    let params: Record<string, unknown> = {};
    let behavior:
      | CardEffectConfig["behavior"]
      | undefined;
    let priority: number | undefined;
    try {
      params = JSON.parse(effectParamsJson || "{}") as Record<string, unknown>;
    } catch {
      toast.error("effect params must be valid JSON");
      throw new Error("invalid json");
    }

    if (effectBehaviorJson.trim()) {
      try {
        behavior = JSON.parse(effectBehaviorJson) as CardEffectConfig["behavior"];
      } catch {
        toast.error("effect behavior must be valid JSON");
        throw new Error("invalid behavior json");
      }
    }

    if (effectPriority.trim()) {
      const parsedPriority = Number.parseInt(effectPriority, 10);
      if (!Number.isFinite(parsedPriority)) {
        toast.error("effect priority must be a number");
        throw new Error("invalid priority");
      }
      priority = parsedPriority;
    }

    return {
      type: effectType,
      target: effectTarget,
      scope: effectScope,
      trigger: effectTrigger,
      params,
      ...(priority !== undefined ? { priority } : {}),
      ...(effectBehaviorModule.trim()
        ? { behaviorModule: effectBehaviorModule.trim() }
        : {}),
      ...(behavior ? { behavior } : {}),
    };
  }

  async function handleSave(): Promise<void> {
    let effect: CardEffectConfig;
    try {
      effect = buildEffect();
    } catch {
      return;
    }
    const tags = tagsStr
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const dur =
      durationHours.trim() === "" ? null : Number.parseFloat(durationHours);
    const mph = Math.max(1, Number.parseInt(maxPerHand, 10) || 1);

    try {
      if (creatingNew) {
        if (!slug.trim() || !name.trim() || !description.trim()) {
          toast.error("slug, name, and description are required");
          return;
        }
        await createCard({
          slug: slug.trim(),
          name: name.trim(),
          description: description.trim(),
          flavor: flavor.trim(),
          rarity,
          category: category.trim(),
          tags,
          emoji: emoji.trim(),
          durationHours: dur,
          droppable,
          stackable,
          maxPerHand: mph,
          effect,
          imagePrompt: imagePrompt.trim() || undefined,
        }).unwrap();
        toast.success("Card created");
        setCreatingNew(false);
      } else {
        await updateCard({
          slug,
          body: {
            name: name.trim(),
            description: description.trim(),
            flavor: flavor.trim(),
            rarity,
            category: category.trim(),
            tags,
            emoji: emoji.trim(),
            durationHours: dur,
            droppable,
            stackable,
            maxPerHand: mph,
            effect,
            imagePrompt: imagePrompt.trim() || null,
          },
        }).unwrap();
        toast.success("Card updated");
      }
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "data" in e ? String((e as { data?: { message?: string } }).data?.message) : "Save failed";
      toast.error(msg || "Save failed");
    }
  }

  function toastApiError(e: unknown, fallback: string): void {
    const msg =
      e && typeof e === "object" && "data" in e
        ? (e as { data?: { message?: string } }).data?.message
        : undefined;
    toast.error(msg || fallback);
  }

  async function handleGeneratePrompt(ai: boolean): Promise<void> {
    if (!slug.trim()) {
      toast.error("enter a slug or select a card from the table");
      return;
    }
    if (creatingNew) {
      toast.error("save the card to the database first — art is stored on the mongo record");
      return;
    }
    try {
      const res = await genPrompt({ slug: slug.trim(), useAi: ai }).unwrap();
      setImagePrompt(res.prompt);
      toast.success(
        ai ? "prompt generated (style template + AI scene)" : "prompt generated (full template)",
      );
    } catch (e) {
      toastApiError(e, "could not generate prompt");
    }
  }

  async function handleGenerateImages(count: 1 | 2): Promise<void> {
    if (!slug.trim() || !imagePrompt.trim()) {
      toast.error("need slug and prompt");
      return;
    }
    if (creatingNew) {
      toast.error("save the card to the database first — generate-images needs a mongo row");
      return;
    }
    try {
      pendingImageGenCount.current = count;
      setLightboxSrc(null);
      const res = await genImages({
        slug: slug.trim(),
        prompt: imagePrompt.trim(),
        ...(count === 1 ? { count: 1 as const } : {}),
      }).unwrap();
      setImagePreviews(res.images);
      toast.success(count === 1 ? "generated 1 image — pick or regenerate" : "generated 2 options — pick one");
    } catch (e) {
      toastApiError(e, "image generation failed");
    }
  }

  async function handlePickImage(index: number): Promise<void> {
    if (!slug.trim()) {
      toast.error("need a slug");
      return;
    }
    if (creatingNew) {
      toast.error("save the card to the database first before attaching art");
      return;
    }
    const dataUrl = imagePreviews[index];
    if (!dataUrl) {
      toast.error("generate images first");
      return;
    }
    try {
      // send pixels in the body — avoids in-memory session (broken with multiple node workers or after expiry)
      await selectImage({
        slug: slug.trim(),
        imageBase64: dataUrl,
        imagePrompt: imagePrompt.trim() || undefined,
      }).unwrap();
      toast.success("image saved to card");
      setImagePreviews([]);
      setLightboxSrc(null);
    } catch (e) {
      toastApiError(e, "could not save image");
    }
  }

  async function handleRecommend(): Promise<void> {
    try {
      const res = await recommendCard({ idea: recommendIdea.trim() || undefined }).unwrap();
      const rec = res.recommendation;
      if (!isRecommendedRow(rec)) {
        toast.error("bad recommendation shape");
        return;
      }
      setCreatingNew(true);
      setSlug(rec.slug);
      setName(rec.name);
      setDescription(rec.description);
      setFlavor(rec.flavor);
      setRarity(rec.rarity);
      setCategory(rec.category);
      setTagsStr((rec.tags ?? []).join(", "));
      setEmoji("🃏");
      setDurationHours(rec.durationHours === null ? "" : String(rec.durationHours));
      setDroppable(rec.droppable !== false);
      setStackable(Boolean(rec.stackable));
      setMaxPerHand(String(rec.maxPerHand ?? 1));
      if (rec.effect) {
        setSelectedPresetId(inferCardEditorPresetId(rec.effect, rec.category));
        setEffectType(rec.effect.type);
        setEffectTarget(rec.effect.target);
        setEffectScope(rec.effect.scope);
        setEffectTrigger(rec.effect.trigger);
        setEffectParamsJson(JSON.stringify(rec.effect.params ?? {}, null, 2));
        setEffectPriority(
          typeof rec.effect.priority === "number" ? String(rec.effect.priority) : "",
        );
        setEffectBehaviorModule(rec.effect.behaviorModule ?? "");
        setEffectBehaviorJson(
          rec.effect.behavior ? JSON.stringify(rec.effect.behavior, null, 2) : "",
        );
      } else {
        setSelectedPresetId("custom");
        setEffectPriority("");
      }
      setImagePrompt(rec.imagePrompt ?? "");
      setImagePreviews([]);
      toast.success("recommendation loaded into editor — review and save");
    } catch {
      toast.error("recommendation failed");
    }
  }

  async function refreshRuntimePanels(): Promise<void> {
    try {
      await Promise.all([
        refetchRuntimeAotd(),
        refetchRuntimeEffects(),
        refetchRuntimeEvents(),
        refetchRuntimeActions(),
      ]);
    } catch {
      toast.error("runtime refresh failed");
    }
  }

  if (user.bootstrapStatus !== "ready") {
    return null;
  }

  if (!isAdmin) {
    return <Navigate to="/user-stats" replace />;
  }

  return (
    <PageContainer className="cardsa-page">
      <style>{`
        @media (max-width: 700px) {
          .cardsa-page { padding: 12px 8px !important; }
          .cardsa-row { flex-wrap: wrap !important; gap: 8px !important; }
          .cardsa-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .cardsa-table-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch !important; margin: 0 -8px !important; }
          .cardsa-grid-1 { grid-template-columns: 1fr !important; }
          .cardsa-card-grid { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important; }
          .cardsa-textarea-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .cardsa-modal { width: min(640px, calc(100vw - 24px)) !important; max-width: 100% !important; box-sizing: border-box !important; }
          .cardsa-tabs { flex-wrap: wrap !important; gap: 4px !important; }
          .cardsa-img-fit { max-width: 100% !important; height: auto !important; display: block !important; }
          .cardsa-page label { min-width: 0 !important; flex: 1 1 100% !important; }
          .cardsa-json { white-space: pre-wrap !important; word-break: break-word !important; max-width: 100% !important; display: block !important; }
          .cardsa-editor-bar { flex-wrap: wrap !important; gap: 8px !important; }
          .cardsa-catalog-header { flex-wrap: wrap !important; gap: 8px !important; }
        }
      `}</style>
      <Title>Cards admin</Title>
      <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "1rem" }}>
        manage definitions, ai art, and recommendations. users open{" "}
        <Link to="/cards" style={{ color: "#663399" }}>
          /cards
        </Link>
        . run backend <code>pnpm cards:migrate-catalog</code> once to seed mongo from the static catalog.
      </p>

      <div
        className="cardsa-tabs"
        role="tablist"
        aria-label="Cards admin tabs"
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid #d1c8e7",
          marginBottom: 16,
        }}
      >
        {TAB_OPTIONS.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "8px 16px",
                fontSize: "0.9rem",
                fontWeight: isActive ? 700 : 500,
                color: isActive ? "#3730a3" : "#555",
                background: isActive ? "#fff" : "transparent",
                border: "1px solid transparent",
                borderColor: isActive ? "#d1c8e7" : "transparent",
                borderBottom: isActive ? "1px solid #fff" : "1px solid transparent",
                borderRadius: "6px 6px 0 0",
                marginBottom: -1,
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "overview" ? <OverviewTab /> : null}
      {activeTab === "schedule" ? <ScheduleTab /> : null}
      {activeTab === "drop" ? <DropControlTab /> : null}
      {activeTab === "sim" && IS_DEV ? (
        <div style={{ overflowX: "auto" }}>
          <DropLobbySimPanel />
        </div>
      ) : null}
      {activeTab === "users" ? <UsersTab /> : null}
      {activeTab === "links" ? <LinksTab /> : null}

      {activeTab === "game" ? (
      <Section>
        <Row style={{ alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
          <SectionTitle style={{ marginBottom: 0 }}>
            Game cards{" "}
            <span className="respect-admin-catalog__count">
              ({agentInfluenceCards.length})
            </span>
          </SectionTitle>
          <Link to="/agent-game" style={{ color: "#663399", fontSize: "0.84rem" }}>
            open /agent-game →
          </Link>
        </Row>
        <InfoCard>
          <SubTitle>How agent-influence cards work</SubTitle>
          <p style={{ fontSize: "0.84rem", color: "#555", margin: 0, lineHeight: 1.55 }}>
            These cards are a separate card family using category{" "}
            <Mono>agent-influence</Mono> and effect type <Mono>agent_influence</Mono>. They do not
            run through the respect/AOTD runtime effect engine — the agent-game service uses their
            slugs as role seats. Players claim a role, write a public prompt, keep a secret choice,
            and the winning choice pays out respect when the game completes.
          </p>
        </InfoCard>

        {agentInfluenceCards.length === 0 ? (
          <p style={{ fontSize: "0.88rem", color: "#666" }}>no agent-influence cards found.</p>
        ) : (
          <div className="respect-admin-gallery cardsa-card-grid" style={{ marginBottom: 12 }}>
            {agentInfluenceCards.map((card) => (
              <div className="respect-admin-gallery__cell" key={card.slug ?? card.id}>
                <button
                  type="button"
                  className="respect-admin-gallery__card-btn"
                  onClick={() => loadCardIntoEditor(card)}
                  title={`load ${card.slug ?? card.id}`}
                >
                  <RespectCard card={card as CardDefinition} size="md" />
                </button>
              </div>
            ))}
          </div>
        )}

        <SubTitle style={{ marginTop: 18 }}>
          {currentAgentGame ? `Live game: ${currentAgentGame.name}` : "Live game"}
        </SubTitle>
        {currentAgentGameLoading ? (
          <p style={{ fontSize: "0.88rem", color: "#666" }}>loading live game…</p>
        ) : currentAgentGame ? (
          <div className="cardsa-table-wrap">
            <Table>
              <thead>
                <tr>
                  <th>role</th>
                  <th>card slug</th>
                  <th>hidden trait</th>
                  <th>player</th>
                  <th>prompt</th>
                </tr>
              </thead>
              <tbody>
                {currentAgentGame.config.slots.map((slot) => (
                  <tr key={slot.roleSlug}>
                    <td>{slot.roleName}</td>
                    <td>
                      <Mono>{slot.roleSlug}</Mono>
                    </td>
                    <td>{slot.hiddenTrait ?? "—"}</td>
                    <td>{slot.username ?? "open"}</td>
                    <td style={{ maxWidth: 320 }}>{slot.prompt ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <p style={{ fontSize: "0.88rem", color: "#666", margin: 0 }}>
            no live agent game right now. The backend still uses these card slugs as the allowed
            role choices when an admin creates a game.
          </p>
        )}
      </Section>
      ) : null}

      {activeTab === "runtime" ? (
      <Section>
        <SectionTitle>Runtime inspector</SectionTitle>
        <p style={{ fontSize: "0.84rem", color: "#666", margin: "0 0 12px", lineHeight: 1.45 }}>
          live Mongo-backed state for the card engine: today&apos;s AOTD workflow, active effects,
          recent domain events, and scheduled expirations.
        </p>
        <Row>
          <Btn
            type="button"
            variant="ghost"
            onClick={() => void refreshRuntimePanels()}
            disabled={
              runtimeAotdLoading ||
              runtimeEffectsLoading ||
              runtimeEventsLoading ||
              runtimeActionsLoading
            }
          >
            refresh runtime
          </Btn>
        </Row>
        <RuntimeGrid className="cardsa-card-grid">
          <RuntimeTile>
            <RuntimeKicker>Today state</RuntimeKicker>
            <RuntimeValue>{runtimeAotdData?.dayState?.state ?? "—"}</RuntimeValue>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              day key: <Mono>{runtimeAotdData?.dayState?.dayKey ?? "—"}</Mono>
            </div>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              scheduled: {formatDateTime(runtimeAotdData?.dayState?.scheduledTime)}
            </div>
          </RuntimeTile>
          <RuntimeTile>
            <RuntimeKicker>Claim owner</RuntimeKicker>
            <RuntimeValue>{runtimeAotdData?.dayState?.claimedByUsername ?? "open"}</RuntimeValue>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              claimed: {formatDateTime(runtimeAotdData?.dayState?.claimedAt)}
            </div>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              aotd id: <Mono>{runtimeAotdData?.dayState?.aotdId ?? "—"}</Mono>
            </div>
          </RuntimeTile>
          <RuntimeTile>
            <RuntimeKicker>Last blocked claim</RuntimeKicker>
            <RuntimeValue>{runtimeAotdData?.dayState?.lastClaimBlockedReason ?? "none"}</RuntimeValue>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              at: {formatDateTime(runtimeAotdData?.dayState?.lastClaimBlockedAt)}
            </div>
          </RuntimeTile>
          <RuntimeTile>
            <RuntimeKicker>Runtime counts</RuntimeKicker>
            <RuntimeValue>
              {(runtimeAotdData?.activeEffects?.length ?? 0)} aotd effects
            </RuntimeValue>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              {runtimeEvents.length} recent events
            </div>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              {runtimeActions.length} scheduled actions
            </div>
          </RuntimeTile>
        </RuntimeGrid>

        <SectionTitle style={{ fontSize: "1rem" }}>AOTD effects</SectionTitle>
        <div className="cardsa-table-wrap">
        <Table>
          <thead>
            <tr>
              <th>effect</th>
              <th>owner</th>
              <th>priority</th>
              <th>mode</th>
              <th>expires</th>
            </tr>
          </thead>
          <tbody>
            {(runtimeAotdData?.activeEffects ?? []).length === 0 ? (
              <tr>
                <td colSpan={5}>no active AOTD effects</td>
              </tr>
            ) : (
              (runtimeAotdData?.activeEffects ?? []).map((effect) => (
                <tr key={effect._id}>
                  <td>
                    <div>{effect.effectType}</div>
                    <div style={{ fontSize: "0.78rem", color: "#666" }}>
                      <Mono>{effect.sourceCardSlug}</Mono>
                    </div>
                  </td>
                  <td>{effect.ownerUsername ?? "—"}</td>
                  <td>{effect.priority}</td>
                  <td>{effect.resolutionMode}</td>
                  <td>{formatDateTime(effect.expiresAt ?? null)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        </div>

        <SectionTitle style={{ fontSize: "1rem", marginTop: 16 }}>All runtime effects</SectionTitle>
        <div className="cardsa-table-wrap">
        <Table>
          <thead>
            <tr>
              <th>effect</th>
              <th>scope</th>
              <th>owner</th>
              <th>status</th>
              <th>expires</th>
            </tr>
          </thead>
          <tbody>
            {runtimeEffects.length === 0 ? (
              <tr>
                <td colSpan={5}>no runtime effects found</td>
              </tr>
            ) : (
              runtimeEffects.map((effect) => (
                <tr key={effect._id}>
                  <td>{effect.effectType}</td>
                  <td><Mono>{effect.scope}</Mono></td>
                  <td>{effect.ownerUsername ?? "—"}</td>
                  <td>{effect.status}</td>
                  <td>{formatDateTime(effect.expiresAt ?? null)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        </div>

        <SectionTitle style={{ fontSize: "1rem", marginTop: 16 }}>Recent event log</SectionTitle>
        <div className="cardsa-table-wrap">
        <Table>
          <thead>
            <tr>
              <th>type</th>
              <th>actor</th>
              <th>when</th>
              <th>stream</th>
              <th>data</th>
            </tr>
          </thead>
          <tbody>
            {runtimeEvents.length === 0 ? (
              <tr>
                <td colSpan={5}>no recorded events yet</td>
              </tr>
            ) : (
              runtimeEvents.map((event) => (
                <tr key={event._id}>
                  <td>{event.eventType}</td>
                  <td>{event.actorUsername ?? event.actorId}</td>
                  <td>{formatDateTime(event.occurredAt)}</td>
                  <td><Mono>{event.streamId ?? "—"}</Mono></td>
                  <td><Mono className="cardsa-json">{previewJson(event.metadata)}</Mono></td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        </div>

        <SectionTitle style={{ fontSize: "1rem", marginTop: 16 }}>Scheduled actions</SectionTitle>
        <div className="cardsa-table-wrap">
        <Table>
          <thead>
            <tr>
              <th>type</th>
              <th>status</th>
              <th>run at</th>
              <th>attempts</th>
              <th>payload</th>
            </tr>
          </thead>
          <tbody>
            {runtimeActions.length === 0 ? (
              <tr>
                <td colSpan={5}>no scheduled actions</td>
              </tr>
            ) : (
              runtimeActions.map((action) => (
                <tr key={action._id}>
                  <td>{action.actionType}</td>
                  <td>{action.status}</td>
                  <td>{formatDateTime(action.runAt)}</td>
                  <td>
                    {action.attempts}/{action.maxAttempts}
                  </td>
                  <td><Mono className="cardsa-json">{previewJson(action.payload)}</Mono></td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        </div>
      </Section>
      ) : null}

      {activeTab !== "catalog" ? null : (
      <>
      <Section>
        <div className="respect-admin-catalog__header cardsa-catalog-header">
          <div>
            <SectionTitle style={{ marginBottom: 2 }}>
              Card definitions{" "}
              <span className="respect-admin-catalog__count">
                ({adminLoading ? "…" : galleryFilteredCards.length}
                {adminLoading ? "" : ` / ${adminCards.length}`})
              </span>
            </SectionTitle>
            <p className="respect-admin-catalog__subtitle">
              click a card to open the editor inline. includes database rows and any cards still only in{" "}
              <code>cardCatalog.ts</code> until you migrate.
            </p>
          </div>
          <Btn type="button" variant="primary" onClick={startNewCard}>
            + new card
          </Btn>
        </div>

        <Details className="respect-admin-catalog__ai">
          <summary>✨ ask AI for a card idea</summary>
          <Row style={{ marginTop: 10, marginBottom: 0, alignItems: "stretch" }}>
            <TextArea
              className="cardsa-textarea-full"
              value={recommendIdea}
              onChange={(e) => setRecommendIdea(e.target.value)}
              rows={2}
              placeholder="seed idea (optional) — e.g. a card that swaps two players' hands"
              style={{ flex: 1, minWidth: 240 }}
            />
            <Btn
              type="button"
              variant="primary"
              onClick={() => void handleRecommend()}
              style={{ alignSelf: "stretch" }}
            >
              suggest
            </Btn>
          </Row>
        </Details>

        {adminLoading ? (
          <p>loading…</p>
        ) : (
          <>
            <div className="respect-admin-catalog__toolbar cardsa-row">
              <div
                className="respect-admin-gallery__chips"
                role="group"
                aria-label="filter by rarity"
              >
                {GALLERY_RARITY_ORDER.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`respect-admin-gallery__chip${galleryRarity === r ? " is-active" : ""}`}
                    onClick={() => setGalleryRarity((prev) => (prev === r ? "" : r))}
                  >
                    {r} ({galleryRarityCounts[r] ?? 0})
                  </button>
                ))}
              </div>
              <div className="respect-admin-catalog__toolbar-fields">
                <Select
                  className="cardsa-input-full"
                  aria-label="category"
                  value={galleryCategory}
                  onChange={(e) => setGalleryCategory(e.target.value)}
                  style={{ minWidth: 160 }}
                >
                  <option value="">all categories</option>
                  {categorySuggestions.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
                <Input
                  className="cardsa-input-full"
                  aria-label="search by name or slug"
                  value={gallerySearch}
                  onChange={(e) => setGallerySearch(e.target.value)}
                  placeholder="search name or slug…"
                  style={{ flex: 1, minWidth: 200 }}
                />
                <Btn
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setGalleryRarity("");
                    setGalleryCategory("");
                    setGallerySearch("");
                  }}
                  disabled={!galleryRarity && !galleryCategory && !gallerySearch}
                >
                  clear
                </Btn>
              </div>
            </div>
            {galleryFilteredCards.length === 0 ? (
              <p style={{ fontSize: "0.88rem", color: "#666" }}>no cards match these filters.</p>
            ) : (
              <div className="respect-admin-gallery cardsa-card-grid">
                {galleryFilteredCards.map((c) => {
                  const key = c.slug ?? c.id;
                  const inDb = c.persistedInDb !== false;
                  const inactive = inDb && c.isActive === false;
                  const isSelected = selectedEditingSlug === key;
                  return (
                    <React.Fragment key={key}>
                      <div
                        className={`respect-admin-gallery__cell${isSelected ? " is-selected" : ""}`}
                      >
                        <button
                          type="button"
                          className="respect-admin-gallery__card-btn"
                          onClick={() => loadCardIntoEditor(c)}
                          title={isSelected ? `close ${key}` : `edit ${key}`}
                          aria-expanded={isSelected}
                        >
                          <RespectCard card={c as CardDefinition} size="md" />
                        </button>
                        <div className="respect-admin-gallery__meta">
                          <span
                            className={`respect-admin-gallery__badge${
                              inDb ? "" : " respect-admin-gallery__badge--warn"
                            }`}
                          >
                            {inDb ? "database" : "file only"}
                          </span>
                          {inDb ? (
                            <span className="respect-admin-gallery__badge">
                              {inactive ? "inactive" : "active"}
                            </span>
                          ) : null}
                          {c.imageUrl ? (
                            <span className="respect-admin-gallery__badge">image</span>
                          ) : null}
                        </div>
                        {inDb ? (
                          <div className="respect-admin-gallery__cell-actions">
                            <Btn
                              type="button"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                void toggleCard(c.slug ?? c.id)
                                  .unwrap()
                                  .then(() => toast.success("toggled"))
                                  .catch(() => toast.error("toggle failed"));
                              }}
                            >
                              toggle
                            </Btn>
                            <Btn
                              type="button"
                              variant="danger"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const ok = await confirm({
                                  title: "Deactivate card?",
                                  message: `deactivate ${c.slug ?? c.id}?`,
                                  confirmLabel: "Deactivate",
                                  tone: "danger",
                                });
                                if (!ok) return;
                                void deleteCard(c.slug ?? c.id)
                                  .unwrap()
                                  .then(() => toast.success("deactivated"))
                                  .catch(() => toast.error("failed"));
                              }}
                            >
                              deactivate
                            </Btn>
                          </div>
                        ) : (
                          <div className="respect-admin-gallery__cell-actions">
                            <span style={{ fontSize: "0.78rem", color: "#666" }}>
                              click card → save to create
                            </span>
                          </div>
                        )}
                      </div>
                      {isSelected ? (
                        <div
                          className="respect-admin-gallery__editor-row"
                          ref={handleEditorRowRef}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Section>
      </>
      )}

      {(() => {
        if (selectedEditingSlug === null) return null;
        const editorSection = (
          <Section>
            <EditorTopBar className="cardsa-editor-bar">
              <SectionTitle style={{ marginBottom: 0 }}>
                {creatingNew ? "create card" : `edit: ${slug || "—"}`}
              </SectionTitle>
              <EditorTopBarActions>
                <Btn type="button" variant="primary" onClick={() => void handleSave()}>
                  {creatingNew ? "create" : "save changes"}
                </Btn>
                <Btn
                  type="button"
                  variant="ghost"
                  onClick={() => setSelectedEditingSlug(null)}
                  aria-label="close editor"
                >
                  close
                </Btn>
              </EditorTopBarActions>
            </EditorTopBar>
        <EditorLayout className="cardsa-grid-1">
          <div>
            <EditorGroup>
            <EditorGroupTitle>Pattern</EditorGroupTitle>
            <PresetCard>
              <SubTitle>Preset</SubTitle>
              <Row style={{ marginBottom: 0 }}>
                <Label style={{ minWidth: "240px" }}>
                  card pattern
                  <Select
                    className="cardsa-input-full"
                    value={selectedPresetId}
                    onChange={(e) => applyPreset(e.target.value as CardEditorPresetId)}
                  >
                    {CARD_EDITOR_PRESETS.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.label}
                      </option>
                    ))}
                  </Select>
                </Label>
                <Label style={{ flex: 2, minWidth: "280px" }}>
                  selected preset
                  <Input className="cardsa-input-full" value={activePreset.label} readOnly />
                </Label>
              </Row>
              <HelperText>{activePreset.description}</HelperText>
            </PresetCard>
            </EditorGroup>

            <EditorGroup>
            <EditorGroupTitle>Identity</EditorGroupTitle>
            <Row>
              <Label>
                slug
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  disabled={!creatingNew}
                  placeholder="unique_slug"
                />
              </Label>
              {creatingNew ? (
                <Btn type="button" variant="ghost" onClick={syncSlugFromName} disabled={!name.trim()}>
                  sync slug from name
                </Btn>
              ) : null}
              <Label>
                name
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Label>
              <Label>
                rarity
                <Select value={rarity} onChange={(e) => setRarity(e.target.value as CardRarity)}>
                  {(["common", "rare", "epic", "legendary"] as const).map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </Label>
            </Row>
            <Row>
              <Label>
                category
                <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {categoryOptions.map((value) => (
                    <option key={value} value={value}>
                      {optionLabel(value, CARD_CATEGORY_OPTIONS)}
                    </option>
                  ))}
                </Select>
              </Label>
              <Label>
                tags (comma)
                <Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} />
              </Label>
              <Label>
                emoji
                <Input value={emoji} onChange={(e) => setEmoji(e.target.value)} />
              </Label>
            </Row>
            <Label>
              description
              <TextArea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
            </Label>
            <Label>
              flavor
              <TextArea value={flavor} onChange={(e) => setFlavor(e.target.value)} rows={2} />
            </Label>
            <Row>
              <Label>
                duration hours
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={durationHours}
                  onChange={(e) => setDurationHours(e.target.value)}
                  placeholder="leave empty for instant"
                />
              </Label>
              <Label>
                max per hand
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={maxPerHand}
                  onChange={(e) => setMaxPerHand(e.target.value)}
                />
              </Label>
              <Label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={droppable} onChange={(e) => setDroppable(e.target.checked)} />
                droppable
              </Label>
              <Label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={stackable} onChange={(e) => setStackable(e.target.checked)} />
                stackable
              </Label>
            </Row>
            </EditorGroup>

            <EditorGroup>
            <EditorGroupTitle>Effect</EditorGroupTitle>
            <Row>
              <Label>
                type
                <Select
                  value={effectType}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setEffectType(nextType);
                    setSelectedPresetId(
                      inferCardEditorPresetId(
                        {
                          type: nextType,
                          target: effectTarget,
                          scope: effectScope,
                          trigger: effectTrigger,
                          params: parsedEffectParams ?? {},
                        },
                        category,
                      ),
                    );
                  }}
                >
                  {effectTypeOptions.map((value) => (
                    <option key={value} value={value}>
                      {optionLabel(value, KNOWN_EFFECT_TYPE_OPTIONS)}
                    </option>
                  ))}
                </Select>
              </Label>
              <Label>
                target
                <Select
                  value={effectTarget}
                  onChange={(e) => setEffectTarget(e.target.value as CardEffectConfig["target"])}
                >
                  {EFFECT_TARGET_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Label>
              <Label>
                scope
                <Select value={effectScope} onChange={(e) => setEffectScope(e.target.value)}>
                  {effectScopeOptions.map((value) => (
                    <option key={value} value={value}>
                      {optionLabel(value, KNOWN_EFFECT_SCOPE_OPTIONS)}
                    </option>
                  ))}
                </Select>
              </Label>
              <Label>
                trigger
                <Select
                  value={effectTrigger}
                  onChange={(e) => setEffectTrigger(e.target.value as CardEffectConfig["trigger"])}
                >
                  {EFFECT_TRIGGER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Label>
              <Label>
                priority
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={effectPriority}
                  onChange={(e) => setEffectPriority(e.target.value)}
                  placeholder="auto from rarity if empty"
                />
              </Label>
            </Row>

            <InfoCard>
              <SubTitle>Feed visibility</SubTitle>
              <Row style={{ marginBottom: 0 }}>
                <Label>
                  audience
                  <Select
                    value={
                      typeof visibilityParams.audience === "string"
                        ? visibilityParams.audience
                        : "public"
                    }
                    onChange={(e) => updateVisibilityField("audience", e.target.value)}
                  >
                    {VISIBILITY_AUDIENCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Label>
                <Label>
                  surface
                  <Select value={selectedSurface} onChange={(e) => updateVisibilitySurface(e.target.value)}>
                    {SURFACE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Label>
                <Label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={visibilityParams.public !== false}
                    onChange={(e) => updateVisibilityField("public", e.target.checked)}
                  />
                  public feed
                </Label>
                <Label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={visibilityParams.showOwner !== false}
                    onChange={(e) => updateVisibilityField("showOwner", e.target.checked)}
                  />
                  show owner
                </Label>
                <Label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={visibilityParams.showCard !== false}
                    onChange={(e) => updateVisibilityField("showCard", e.target.checked)}
                  />
                  show card
                </Label>
                <Label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={visibilityParams.showTimer !== false}
                    onChange={(e) => updateVisibilityField("showTimer", e.target.checked)}
                  />
                  show timer
                </Label>
              </Row>
              <HelperText>
                These controls feed the public card strip and viewer notices. Hide the owner or the
                card here when the effect should stay partially secret.
              </HelperText>
            </InfoCard>

            {[
              "lock_creation_window",
              "hide_race_time",
              "suppress_other_aotd_notifications",
              "mute_notifications",
            ].includes(effectType) ? (
              <InfoCard>
                <SubTitle>Timed effect window</SubTitle>
                <Row style={{ marginBottom: 0 }}>
                  <Label>
                    duration seconds
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={effectDurationSeconds}
                      onChange={(e) => setScalarEffectParam("durationSeconds", e.target.value, "number")}
                      placeholder="30"
                    />
                  </Label>
                  {effectType === "lock_creation_window" ? (
                    <Label style={{ flex: 2 }}>
                      blocked message
                      <TextArea
                        value={effectBlockedMessage}
                        onChange={(e) => setScalarEffectParam("blockedMessage", e.target.value, "string")}
                        rows={2}
                        placeholder="Shown to users who are blocked from claiming."
                      />
                    </Label>
                  ) : null}
                </Row>
              </InfoCard>
            ) : null}

            {effectType === "respect_multiplier" ? (
              <InfoCard>
                <SubTitle>Respect multiplier config</SubTitle>
                <Row style={{ marginBottom: 0 }}>
                  <Label>
                    multiplier
                    <Input
                      type="number"
                      min="1"
                      step="0.1"
                      value={effectMultiplier}
                      onChange={(e) => setScalarEffectParam("multiplier", e.target.value, "number")}
                    />
                  </Label>
                  <Label>
                    max bonus
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={effectMaxBonus}
                      onChange={(e) => setScalarEffectParam("maxBonus", e.target.value, "number")}
                      placeholder="optional cap"
                    />
                  </Label>
                  <Label>
                    recipient field
                    <Select
                      value={effectRecipientField}
                      onChange={(e) => setScalarEffectParam("recipientField", e.target.value, "string")}
                    >
                      {RECIPIENT_FIELD_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Label>
                </Row>
              </InfoCard>
            ) : null}

            {effectType === "respect_bonus" ? (
              <InfoCard>
                <SubTitle>Respect bonus config</SubTitle>
                <Row style={{ marginBottom: 0 }}>
                  <Label>
                    reward respect
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={rewardRespect}
                      onChange={(e) => setRewardRespect(e.target.value)}
                    />
                  </Label>
                  <Label>
                    recipient field
                    <Select
                      value={effectRecipientField}
                      onChange={(e) => setScalarEffectParam("recipientField", e.target.value, "string")}
                    >
                      {RECIPIENT_FIELD_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Label>
                  <Label>
                    ownership check
                    <Select
                      value={effectOwnershipCheck}
                      onChange={(e) => setScalarEffectParam("ownershipCheck", e.target.value, "string")}
                    >
                      {OWNERSHIP_CHECK_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Label>
                  <Label>
                    consume rule
                    <Select
                      value={effectConsumeOn}
                      onChange={(e) => setScalarEffectParam("consumeOn", e.target.value, "string")}
                    >
                      {CONSUME_ON_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Label>
                </Row>
              </InfoCard>
            ) : null}

            {effectType === "pin_comment" ? (
              <InfoCard>
                <SubTitle>Pin config</SubTitle>
                <Row style={{ marginBottom: 0 }}>
                  <Label>
                    pin duration hours
                    <Input
                      type="number"
                      min="0"
                      step="0.25"
                      value={effectPinDurationHours}
                      onChange={(e) => setScalarEffectParam("pinDurationHours", e.target.value, "number")}
                    />
                  </Label>
                </Row>
              </InfoCard>
            ) : null}

            {effectType === "highlight_comment" ? (
              <InfoCard>
                <SubTitle>Highlight config</SubTitle>
                <Row style={{ marginBottom: 0 }}>
                  <Label>
                    highlight duration hours
                    <Input
                      type="number"
                      min="0"
                      step="0.25"
                      value={effectHighlightDurationHours}
                      onChange={(e) => setScalarEffectParam("highlightDurationHours", e.target.value, "number")}
                    />
                  </Label>
                </Row>
              </InfoCard>
            ) : null}

            {effectType === "transfer_respect" ? (
              <InfoCard>
                <SubTitle>Transfer config</SubTitle>
                <Row style={{ marginBottom: 0 }}>
                  <Label>
                    amount
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={effectTransferAmount}
                      onChange={(e) => setScalarEffectParam("amount", e.target.value, "number")}
                    />
                  </Label>
                  <Label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={effectTaxFree}
                      onChange={(e) =>
                        updateEffectParams((draft) => {
                          if (e.target.checked) {
                            draft.taxFree = true;
                          } else {
                            delete draft.taxFree;
                          }
                        })
                      }
                    />
                    tax free
                  </Label>
                </Row>
              </InfoCard>
            ) : null}

            {effectType === "agent_influence" ? (
              <InfoCard>
                <SubTitle>Game-only role card</SubTitle>
                <HelperText style={{ marginTop: 0 }}>
                  This card is used by the agent game as a role slug. The respect engine ignores it
                  during normal card activation, and the live game slots reference its slug directly.
                </HelperText>
              </InfoCard>
            ) : null}

            <Row>
              <Label>
                behavior module
                <Select
                  value={effectBehaviorModule}
                  onChange={(e) => setEffectBehaviorModule(e.target.value)}
                >
                  <option value="">none</option>
                  {behaviorModuleOptions.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              </Label>
            </Row>

            <Details>
              <summary>Advanced effect JSON</summary>
              {effectParamsError ? (
                <HelperText style={{ color: "#c62828" }}>
                  params JSON is invalid right now: {effectParamsError}
                </HelperText>
              ) : (
                <HelperText>
                  Use the selectors above for normal work. Keep the raw JSON here for rare fields or
                  custom behaviors only.
                </HelperText>
              )}
              <Label>
                params (JSON)
                <TextArea className="cardsa-textarea-full cardsa-json" value={effectParamsJson} onChange={(e) => setEffectParamsJson(e.target.value)} rows={8} />
              </Label>
              <Label>
                behavior (JSON, optional)
                <TextArea
                  className="cardsa-textarea-full cardsa-json"
                  value={effectBehaviorJson}
                  onChange={(e) => setEffectBehaviorJson(e.target.value)}
                  rows={6}
                  placeholder='{"shouldActivate":"return true;","execute":"return { applied: true, log: \"...\" };"}'
                />
              </Label>
            </Details>
            </EditorGroup>

            <EditorGroup>
            <EditorGroupTitle>Impact</EditorGroupTitle>
            <CardImpactPanel slug={slug} />
            </EditorGroup>

            <EditorGroup>
            <EditorGroupTitle>Card art</EditorGroupTitle>
        {creatingNew ? (
          <p style={{ fontSize: "0.82rem", color: "#7a4a00", marginBottom: 12, lineHeight: 1.45 }}>
            save this card to the database first (create or migrate from catalog). prompts and uploads need a mongo row;
            picking a generated image sends it as base64 so it still saves if the server runs multiple workers.
          </p>
        ) : null}
        <Row>
          <Btn
            type="button"
            variant="ghost"
            onClick={() => void handleGeneratePrompt(true)}
            disabled={!slug.trim() || creatingNew || isGeneratingPrompt}
          >
            {isGeneratingPrompt ? "generating prompt…" : "generate prompt (ai)"}
          </Btn>
          <Btn
            type="button"
            variant="ghost"
            onClick={() => void handleGeneratePrompt(false)}
            disabled={!slug.trim() || creatingNew || isGeneratingPrompt}
          >
            {isGeneratingPrompt ? "generating prompt…" : "generate prompt (template)"}
          </Btn>
        </Row>
        <Label>
          image prompt (edit before generating)
          <TextArea
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
            rows={4}
            disabled={isGeneratingImages}
          />
        </Label>
        <Row>
          <Btn
            type="button"
            variant="primary"
            onClick={() => void handleGenerateImages(1)}
            disabled={!slug.trim() || creatingNew || isGeneratingImages || isGeneratingPrompt}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {isGeneratingImages ? (
              <>
                <InlineSpinner aria-hidden />
                generating…
              </>
            ) : (
              "generate 1 image"
            )}
          </Btn>
          <Btn
            type="button"
            variant="primary"
            onClick={() => void handleGenerateImages(2)}
            disabled={!slug.trim() || creatingNew || isGeneratingImages || isGeneratingPrompt}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {isGeneratingImages ? (
              <>
                <InlineSpinner aria-hidden />
                generating…
              </>
            ) : (
              "generate 2 images"
            )}
          </Btn>
        </Row>
        {isGeneratingImages ? (
          <ArtStatus role="status" aria-live="polite">
            <span>
              this usually takes 1–3 minutes; you&apos;ll see {pendingImageGenCount.current === 1 ? "one thumbnail" : "two thumbnails"} here when done.
            </span>
          </ArtStatus>
        ) : null}
        {imagePreviews.length > 0 ? (
          <>
            <p style={{ fontSize: "0.78rem", color: "#666", margin: "10px 0 0" }}>
              expand icon opens full size; click the thumbnail to set it as card art.
            </p>
            <ImagePick>
              {imagePreviews.map((src, i) => (
                <ImagePreviewTile key={i}>
                  <PickImageButton
                    type="button"
                    onClick={() => void handlePickImage(i)}
                    disabled={isSavingArtImage}
                    title={isSavingArtImage ? "uploading…" : `use option ${i + 1} as card art`}
                  >
                    <img className="cardsa-img-fit" src={src} alt={`option ${i + 1} thumbnail`} />
                  </PickImageButton>
                  <ExpandImageButton
                    type="button"
                    onClick={() => setLightboxSrc(src)}
                    disabled={isSavingArtImage}
                    title="view full size"
                    aria-label={`view option ${i + 1} full size`}
                  >
                    <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
                  </ExpandImageButton>
                </ImagePreviewTile>
              ))}
            </ImagePick>
          </>
        ) : null}
        {isSavingArtImage ? (
          <ArtStatus role="status" aria-live="polite">
            <InlineSpinner aria-hidden />
            <span>uploading to storage and updating the card…</span>
          </ArtStatus>
        ) : null}
            </EditorGroup>
          </div>
          <PreviewAside>
            <SectionTitle style={{ fontSize: "1rem", marginBottom: 8 }}>live preview</SectionTitle>
            <p style={{ fontSize: "0.78rem", color: "#666", margin: "0 0 12px" }}>
              same <code style={{ fontSize: "0.85em" }}>RespectCard</code> as{" "}
              <Link to="/cards" style={{ color: "#663399" }}>
                /cards
              </Link>{" "}
              and the AOTD strip.
            </p>
            <PreviewWrap>
              <RespectCard card={previewCard} size="lg" />
            </PreviewWrap>
            {imagePreviews.length > 1 ? (
              <p style={{ fontSize: "0.75rem", color: "#888", marginTop: 10, lineHeight: 1.4 }}>
                preview art shows option 1; pick below to save the chosen image to the card.
              </p>
            ) : null}
          </PreviewAside>
        </EditorLayout>
          </Section>
        );
        if (selectedEditingSlug === "__new__") return editorSection;
        if (inlineEditorTarget) return createPortal(editorSection, inlineEditorTarget);
        // fallback: render in place if the selected card isn't in the visible gallery
        return editorSection;
      })()}

      {activeTab === "catalog" ? (
      <Section>
        <SectionTitle>Today&apos;s drop</SectionTitle>
        {dropLoading ? (
          <p>Loading drop…</p>
        ) : (
          <>
            <p style={{ fontSize: "0.9rem" }}>
              date: <strong>{drop?.date ?? "—"}</strong> — open:{" "}
              <strong>{drop?.isOpen ? "yes" : "no"}</strong> — claims:{" "}
              <strong>{drop?.totalClaims ?? 0}</strong>
            </p>
            <div className="cardsa-table-wrap">
            <Table>
              <tbody>
                {dropCards.map((dc, i) => (
                  <tr key={i}>
                    <td>{dc.card?.name ?? dc.card?.id ?? "—"}</td>
                    <td>
                      {dc.remaining} / {dc.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          </>
        )}
      </Section>
      ) : null}

      {lightboxSrc ? (
        <LightboxOverlay
          role="presentation"
          onClick={() => setLightboxSrc(null)}
        >
          <LightboxPanel
            className="cardsa-modal"
            role="dialog"
            aria-modal="true"
            aria-label="enlarged generated image"
            onClick={(e) => e.stopPropagation()}
          >
            <LightboxClose type="button" onClick={() => setLightboxSrc(null)} aria-label="close">
              ×
            </LightboxClose>
            <LightboxImg className="cardsa-img-fit" src={lightboxSrc} alt="enlarged generated option" />
            <LightboxHint>
              press Escape, click ×, or click the dark area to close. use the thumbnail in the form to
              attach this image to the card.
            </LightboxHint>
          </LightboxPanel>
        </LightboxOverlay>
      ) : null}
    </PageContainer>
  );
};

export default CardsAdminPage;
